import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import './ChatbotWidget.css';

export function transformWreckLinks(content) {
  return content.replace(
    /https:\/\/hylyt\.net\/hylkykortti\/(\d+)/g,
    '[/hylkykortti/$1](/hylkykortti/$1)'
  );
}

const markdownComponents = {
  a({ href, children }) {
    if (href?.startsWith('/hylkykortti/')) {
      return <Link to={href}>{children}</Link>;
    }
    return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
  },
};

const INITIAL_MESSAGE = { role: 'assistant', content: 'Kuinka voin auttaa sinua löytämään hylyn?' };

export default function ChatbotWidget({ apiUrl }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([INITIAL_MESSAGE]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [rateLimitExceeded, setRateLimitExceeded] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const toggleRef = useRef(null);

  useEffect(() => {
    if (open) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
      inputRef.current?.focus();
    }
  }, [messages, loading, open]);

  function handleClose() {
    setOpen(false);
    setTimeout(() => toggleRef.current?.focus(), 0);
  }

  async function sendMessage() {
    const trimmedInput = input.trim();
    if (!trimmedInput || loading || rateLimitExceeded) return;

    const conversationHistory = messages.map((m) => ({ role: m.role, content: m.content }));
    const userMessage = { role: 'user', content: trimmedInput };

    setMessages((m) => [...m, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const res = await fetch(`${apiUrl}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: trimmedInput, conversationHistory }),
      });

      if (res.status === 429) {
        setMessages((m) => [...m, {
          role: 'assistant',
          content: 'Olet lähettänyt tänään 10 viestiä. Päivittäinen raja on täynnä — voit jatkaa huomenna. 🐟',
          warning: true,
        }]);
        setRateLimitExceeded(true);
        return;
      }

      if (res.status === 503) {
        setMessages((m) => [...m, {
          role: 'assistant',
          content: 'Chattibotti ei ole juuri nyt käytettävissä. Yritä uudelleen myöhemmin.',
          warning: true,
        }]);
        return;
      }

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      setMessages((m) => [...m, { role: 'assistant', content: data.message }]);
    } catch {
      setMessages((m) => [
        ...m,
        { role: 'assistant', content: 'Jokin meni pieleen. Yritä uudelleen.' },
      ]);
    } finally {
      setLoading(false);
    }
  }

  if (!open) {
    return (
      <button
        ref={toggleRef}
        className="chatbot-toggle"
        onClick={() => setOpen(true)}
        aria-label="Avaa chatbot"
      >
        💬
      </button>
    );
  }

  return (
    <div className="chatbot-container">
      <div className="chatbot-header">
        <div className="chatbot-header-left">
          <div className="chatbot-avatar">⚓</div>
          <div className="chatbot-header-text">
            <span className="chatbot-header-title">Etsi laivanhylkyjä</span>
            <span className="chatbot-header-sub-full">Puosu Tekoälyavustaja</span>
            <span className="chatbot-header-sub-short">Tekoälyavustaja</span>
          </div>
        </div>
        <button onClick={handleClose} aria-label="Sulje chatbot">
          ✕
        </button>
      </div>

      <div className="chatbot-window" aria-live="polite" aria-label="Chat messages">
        {messages.map((msg, i) => (
          <div key={i} className={`bubble ${msg.role}${msg.warning ? ' warning' : ''}`}>
            {msg.role === 'assistant' ? (
              <ReactMarkdown components={markdownComponents}>
                {transformWreckLinks(msg.content)}
              </ReactMarkdown>
            ) : (
              msg.content
            )}
          </div>
        ))}
        {loading && (
          <div className="typing" aria-label="Kirjoittaa…">
            <div className="typing-dot" />
            <div className="typing-dot" />
            <div className="typing-dot" />
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <div className="chatbot-input">
        <input
          ref={inputRef}
          aria-label="Viesti"
          placeholder="Kysy hylyistä…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
          disabled={loading || rateLimitExceeded}
        />
        <button onClick={sendMessage} disabled={loading || rateLimitExceeded}>
          Lähetä
        </button>
      </div>
    </div>
  );
}
